import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import {NoteLinkedGroupKind, UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import getNoteLinkedObjectsOfTypes from 'notebook/notes/util/getNoteLinkedObjectsOfTypes';
import excess from 'notebook/tables/excess';
import {Note, NoteLinkedObjectType, NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import uniqueId from 'util/uniqueId';

import {EditorMixinInterface} from '../Editor';
import isCutAndPasteWithinSameDocument from '../isCutAndPasteWithinSameDocument';
import convertMixtureGroupForPaste from './convertMixtureGroupForPaste';
import convertNotePartGridForPaste from './convertNotePartGridForPaste';
import convertStoichiometryGroupForPaste from './convertStoichiometryGroupForPaste';

function doesNoteIncludeNoteLinkedGroupUuid(note: Note, provenanceUuid: string): boolean {
  return getNoteLinkedObjectsOfTypes(note, [NoteLinkedObjectType.NOTE_LINKED_GROUP]).some(
    (group) => (group as UnknownNoteLinkedGroup).provenanceUuid === provenanceUuid
  );
}

export function getSourceFileIdFromNoteLinkedGroup(
  group: Pick<UnknownNoteLinkedGroup, 'containedNoteParts'>
): string | undefined {
  for (const containedNotePart of group.containedNoteParts) {
    const fileId = (containedNotePart as {item?: {fileId?: string}}).item?.fileId;
    if (fileId) {
      return fileId;
    }
  }
  return undefined;
}

export default function convertNoteLinkedGroupForPaste(
  item: NotePartWithItem<UnknownNoteLinkedGroup>,
  component: Partial<EditorMixinInterface>,
  usedTableNames: Array<string>,
  destinationFileId: string,
  isDestinationConfig: boolean | undefined,
  note?: Note
): {
  item: NotePartWithItem<UnknownNoteLinkedGroup>;
  name: string | null;
} {
  let name: string | null = item.item.name;
  if (name != null && usedTableNames.includes(name)) {
    name = excess.util.uniqueTableName(usedTableNames, excess.util.extractTableNamePrefix(name));
    item.item.name = name;
  }

  const preserveGroupProvenanceUuid = isCutAndPasteWithinSameDocument(
    note,
    destinationFileId,
    getSourceFileIdFromNoteLinkedGroup(item.item),
    item.item.provenanceUuid,
    doesNoteIncludeNoteLinkedGroupUuid
  );

  if (item.item.kind === NoteLinkedGroupKind.MIXTURE_GROUP) {
    convertMixtureGroupForPaste(
      item,
      name,
      component,
      usedTableNames,
      destinationFileId,
      isDestinationConfig,
      note
    );
  } else if (item.item.kind === NoteLinkedGroupKind.STOICHIOMETRY_GROUP) {
    convertStoichiometryGroupForPaste(
      item,
      name,
      component,
      usedTableNames,
      destinationFileId,
      isDestinationConfig,
      preserveGroupProvenanceUuid,
      note
    );
  } else if (item.item.kind === NoteLinkedGroupKind.NOTE_PART_GRID) {
    convertNotePartGridForPaste(item, component);
  }

  const updatedItem = produce(item, (draftItem) => {
    if (!preserveGroupProvenanceUuid) {
      draftItem.item.provenanceUuid = uuidv4();
    }
    draftItem.item.cid = uniqueId('nlg');
    draftItem.item.isConfig = isDestinationConfig ?? false;
  });

  return {item: updatedItem, name};
}
